/**
 * API client.
 *
 * Directive §47: the session is an HttpOnly `__Host-` cookie. This module never
 * reads or writes document.cookie — the browser attaches it automatically and
 * JavaScript is deliberately unable to see it.
 *
 * Directive §81 / §82: no localStorage, sessionStorage or IndexedDB. The CSRF
 * token is held in a module-scoped variable for the lifetime of the page and is
 * gone on reload, at which point /me issues a fresh one.
 *
 * Directive §48: every state-changing request carries the CSRF header. The
 * Origin header is set by the browser and cannot be forged by page script.
 */

import { isNativeMobile } from '../mobile/platform.js';

async function nativeSession() {
  return (await import('../mobile/secureChannel.js')).mobileSession();
}

async function nativeCallCenterSnapshot<T>(): Promise<T> {
  return (await import('../mobile/secureChannel.js')).mobileSnapshot<T>()
    .then((snapshot) => snapshot.data);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason?: string,
    readonly body?: unknown,
  ) {
    super(`API ${status}${reason ? `: ${reason}` : ''}`);
    this.name = 'ApiError';
  }
}

/** In-memory only. Never persisted. §82 */
let csrfToken: string | null = null;


/*
 * Native WebAuthn registration.
 * The server returns JSON/Base64URL creation options. The browser credential
 * API requires ArrayBuffers. The verification endpoint expects Base64URL JSON.
 */
function b64urlToBytes(value: string): Uint8Array {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
    + '='.repeat((4 - (value.length % 4)) % 4);
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function bytesToB64url(value: ArrayBuffer | ArrayBufferView): string {
  const bytes = value instanceof ArrayBuffer
    ? new Uint8Array(value)
    : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  let raw = '';
  for (let i = 0; i < bytes.length; i += 1) raw += String.fromCharCode(bytes[i]);
  return btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function creationOptionsFromJSON(options: any): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: b64urlToBytes(options.challenge),
    user: {
      ...options.user,
      id: b64urlToBytes(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64urlToBytes(c.id),
    })),
  } as PublicKeyCredentialCreationOptions;
}

function requestOptionsFromJSON(options: any): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: b64urlToBytes(options.challenge),
    allowCredentials: (options.allowCredentials ?? []).map((c: any) => ({
      ...c,
      id: b64urlToBytes(c.id),
    })),
  } as PublicKeyCredentialRequestOptions;
}

async function startNativeAuthentication(optionsJSON: any): Promise<Record<string, unknown>> {
  if (!window.PublicKeyCredential || !navigator.credentials?.get) {
    throw new Error('WEBAUTHN_NOT_SUPPORTED');
  }
  const credential = await navigator.credentials.get({
    publicKey: requestOptionsFromJSON(optionsJSON),
  }) as PublicKeyCredential | null;
  if (!credential) throw new Error('WEBAUTHN_AUTHENTICATION_CANCELLED');
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64url(response.clientDataJSON),
      authenticatorData: bytesToB64url(response.authenticatorData),
      signature: bytesToB64url(response.signature),
      userHandle: response.userHandle ? bytesToB64url(response.userHandle) : undefined,
    },
  };
}

async function startNativeRegistration(
  optionsJSON: any,
): Promise<Record<string, unknown>> {
  if (!window.PublicKeyCredential || !navigator.credentials?.create) {
    throw new Error('WEBAUTHN_NOT_SUPPORTED');
  }

  const credential = await navigator.credentials.create({
    publicKey: creationOptionsFromJSON(optionsJSON),
  }) as PublicKeyCredential | null;

  if (!credential) throw new Error('WEBAUTHN_REGISTRATION_CANCELLED');

  const response = credential.response as AuthenticatorAttestationResponse;

  return {
    id: credential.id,
    rawId: bytesToB64url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64url(response.clientDataJSON),
      attestationObject: bytesToB64url(response.attestationObject),
      transports: typeof response.getTransports === 'function'
        ? response.getTransports()
        : undefined,
    },
  };
}

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * These endpoints are intentionally usable before a session/CSRF token exists.
 * /auth/login creates the session and returns the first csrfToken.
 * /auth/password/forgot is an unauthenticated recovery request.
 * Every other state-changing request still fails closed without x-veritas-csrf.
 */
const PREAUTH_CSRF_EXEMPT = new Set([
  '/auth/login',
  '/auth/password/forgot',
  '/auth/password/accept',
]);

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
  csrfRetry = false,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  if (isNativeMobile()) {
    const mobile = await import('../mobile/secureChannel.js');
    return mobile.mobileOperatorRequest<T>(path, init);
  }
  const headers: Record<string, string> = { accept: 'application/json' };

  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (!SAFE.has(method) && !PREAUTH_CSRF_EXEMPT.has(path)) {
    if (!csrfToken) {
      // A state-changing call before the token exists would be rejected by the
      // server anyway; failing here gives a clearer error than a 403. §48
      throw new ApiError(0, 'CSRF_TOKEN_UNAVAILABLE');
    }
    headers['x-veritas-csrf'] = csrfToken;
  }

  const res = await fetch(path, {
    method,
    headers,
    // Send the session cookie; never follow a cross-origin redirect. §90
    credentials: 'same-origin',
    redirect: 'error',
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const text = await res.text();
  const body = text ? safeParse(text) : undefined;

  // Another open tab may rotate the shared session cookie during MFA or
  // step-up. Its in-memory token then belongs to the prior session. The server
  // returns a replacement only after validating the current session and a
  // permitted Origin. Retry the rejected mutation once with that fresh token.
  const issued = (body as any)?.csrfToken;
  if (typeof issued === 'string') csrfToken = issued;

  if (!res.ok) {
    if (!csrfRetry && res.status === 403 && (body as any)?.error === 'csrf_failed' &&
        typeof issued === 'string' && !SAFE.has(method) && !PREAUTH_CSRF_EXEMPT.has(path)) {
      return request<T>(path, init, true);
    }
    throw new ApiError(res.status, (body as any)?.reason ?? (body as any)?.error, body);
  }

  return body as T;
}

/** CSRF header for multipart requests, which bypass `request`. §48 */
function csrfHeader(): Record<string, string> {
  if (!csrfToken) throw new ApiError(0, 'CSRF_TOKEN_UNAVAILABLE');
  return { 'x-veritas-csrf': csrfToken };
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export interface MeResponse {
  userId: string;
  displayName?: string;
  permissions?: string[];
  mfaLevel: 'none' | 'totp' | 'webauthn';
  roles: Array<{ role: string; scope: string; scopeRef: string | null }>;
  csrfToken: string;
}

export interface FieldWellnessDraft {
  identityPrefill?: import('../field-wellness/identityPrefill.js').WellnessIdentityPrefill;
  draftId?: string | null;
  caseRecordId?: string | null;
  exists: boolean;
  revision: number;
  formVersion: string;
  step: number;
  review: boolean;
  answers: Record<string, string>;
  originalAddress?: string | null;
  originalAddressCaseId?: string | null;
  updatedAt: string | null;
  protectionProfile: 'aag_trident_fips203_204_205_v1';
}

export interface FieldWellnessWorkspace {
  rows: Array<{ caseId: string; caseNumber: string; draftId: string | null;
    updatedAt: string | null; step: number | null; review: boolean;
    lastReference: string | null; lastSubmittedAt: string | null }>;
  legacyDraft: { draftId: string; caseNumber: string | null; updatedAt: string } | null;
  hasMore: boolean;
  offset: number;
  protectionProfile: 'aag_trident_fips203_204_205_v1';
}

export const api = {
  me: () => isNativeMobile()
    ? nativeSession().then((session): MeResponse => ({
        userId: session.userId,
        displayName: session.displayName,
        permissions: session.permissions,
        mfaLevel: session.mfaLevel,
        roles: session.roles,
        csrfToken: '',
      }))
    : request<MeResponse>('/me'),

  home: () => request<import('../screens/Home.js').HomeData>('/home'),

  people: (opts: { q?: string; filter?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (opts.q) qs.set('q', opts.q);
    if (opts.filter && opts.filter !== 'all') qs.set('filter', opts.filter);
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.offset) qs.set('offset', String(opts.offset));
    return request<{
      items: import('../screens/People.js').PersonSummary[];
      total: number;
    }>(`/people?${qs}`);
  },

  visits: (tab: string, opts: { limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams({ tab });
    if (opts.limit) query.set('limit', String(opts.limit));
    if (opts.offset) query.set('offset', String(opts.offset));
    return (
    request<import('../screens/Visits.js').VisitsData>(
      `/visits?${query}`));
  },

  /** Current stop for the signed-in operator's active route. §23 */
  fieldStop: (routePlanId?: string) => request<import('../screens/FieldMode.js').FieldStop>(
    `/field/current${routePlanId ? `?routePlanId=${encodeURIComponent(routePlanId)}` : ''}`),

  completeStop: (body: {
    stopId: string; routePlanId?: string; outcome: string; note: string; recordIds: string[];
  }) => request<{ nextStop: import('../screens/FieldMode.js').FieldStop | null }>(
    '/field/complete', { method: 'POST', body }),

  skipStop: (stopId: string, routePlanId?: string) =>
    request<{ nextStop: import('../screens/FieldMode.js').FieldStop | null }>(
      '/field/skip', { method: 'POST', body: { stopId, routePlanId } }),

  tripCandidates: () => request<{
    visits: import('../screens/TripWizard.js').SelectableVisit[];
    teams: import('../screens/TripWizard.js').SelectableTeam[];
    unresolvedCount: number;
    defaultDate: string;
  }>('/trips/candidates'),

  /**
   * Plan a trip. One call — the optimizer, team partition and segmentation all
   * run server-side, and no routing parameter is exposed to the client. §4 §17
   */
  planTrip: (body: {
    visitIds: string[]; teamIds: string[]; date: string; startTime: string;
  }) => request<import('../screens/TripWizard.js').PlanReview>(
    '/trips/plan', { method: 'POST', body }),

  /**
   * Route segments. A PROTECTED trip returns `segments: []` with a policy
   * reason and no URL — the decision is the server's, and the client simply
   * renders whatever it is told. §22 §70
   */
  routeSegments: (routePlanId: string) =>
    request<{
      segments: Array<{ idx: number; firstSeq: number; lastSeq: number; waypointCount: number }>;
      classification?: 'LOGISTICS' | 'PROTECTED';
      url?: string | null;
      reason?: string;
      message?: string;
    }>(`/routes/segments?routePlanId=${encodeURIComponent(routePlanId)}`),

  // -------------------------------------------------------------------------
  // Authentication. §42 §43 §46
  // -------------------------------------------------------------------------

  signIn: (username: string, password: string) =>
    request<{
      ok: true;
      mfaRequired: boolean;
      mfaLevel: string;
      mustChangePassword: boolean;
      totpEnrolled: boolean;
      webauthnEnrolled: boolean;
      recoveryCodesGenerated: boolean;
      firstSignInRequired: boolean;
    }>('/auth/login', { method: 'POST', body: { username, password } }),

  /** §46: the response is identical for known and unknown addresses. */
  requestPasswordReset: (email: string) =>
    request<{ ok: true }>('/auth/password/forgot', { method: 'POST', body: { email } }),

  acceptPasswordToken: (token: string, password: string) =>
    request<{
      ok: true;
      firstSignInRequired: boolean;
      totpEnrolled: boolean;
      webauthnEnrolled: boolean;
      recoveryCodesGenerated: boolean;
    }>('/auth/password/accept', { method: 'POST', body: { token, password } }),

  setInitialPassword: (password: string) =>
    request<{ ok: true }>('/auth/password/initial', { method: 'POST', body: { password } }),

  setupTotp: () =>
    request<{ secret: string; uri: string }>(
      '/auth/totp/setup', { method: 'POST', body: {} }),

  /** §43: 'sms' is not a member of this union and no such endpoint exists. */
  verifyMfa: (method: 'webauthn' | 'totp' | 'recovery_code', code: string) =>
    request<{ ok: true }>('/auth/mfa/verify', { method: 'POST', body: { method, code } }),

  verifyWebAuthn: async () => {
    const options = await request<any>('/auth/webauthn/challenge', {
      method: 'POST', body: { purpose: 'authentication' },
    });
    const response = await startNativeAuthentication(options);
    return request<{ ok: true; mfaLevel: string }>('/auth/mfa/verify', {
      method: 'POST',
      body: { method: 'webauthn', challenge: options.challenge, response },
    });
  },

  beginWebAuthnStepUp: () =>
    request<any>('/auth/webauthn/challenge', {
      method: 'POST', body: { purpose: 'step_up' },
    }),

  completeWebAuthnStepUp: async (options: any) => {
    const response = await startNativeAuthentication(options);
    return request<{ ok: true; mfaLevel: 'webauthn'; csrfToken: string }>(
      '/auth/mfa/verify',
      {
        method: 'POST',
        body: {
          method: 'webauthn', purpose: 'step_up',
          challenge: options.challenge, response,
        },
      },
    );
  },

  enrollSecurityKey: async (name: string) => {
    const options = await request<any>(
      '/auth/webauthn/challenge',
      { method: 'POST', body: { purpose: 'registration' } },
    );
    const response = await startNativeRegistration(options);
    return request<{ ok: true; mfaLevel: string }>(
      '/auth/webauthn/enroll',
      {
        method: 'POST',
        body: { name, challenge: options.challenge, response },
      },
    );
  },

  deferSecurityKey: () =>
    request<{ ok: true }>('/auth/webauthn/defer', { method: 'POST', body: {} }),

  regenerateRecoveryCodes: () =>
    request<{ codes: string[] }>('/auth/recovery-codes', { method: 'POST' }),

  // -------------------------------------------------------------------------
  // Records and routes
  // -------------------------------------------------------------------------

  person: (id: string) =>
    request<import('../screens/Person.js').PersonDetail>(`/people/${encodeURIComponent(id)}`),

  logCall: (personId: string, body: import('../screens/Person.js').CallLogState) =>
    request<{ ok: true }>(`/people/${encodeURIComponent(personId)}/calls`,
      { method: 'POST', body }),

  scheduleVisit: (
    personId: string, body: import('../screens/Person.js').ScheduleVisitState,
  ) => request<{ ok: true }>(`/people/${encodeURIComponent(personId)}/visits`,
    { method: 'POST', body }),

  addNote: (personId: string, note: string) =>
    request<{ id: string }>(`/people/${encodeURIComponent(personId)}/notes`,
      { method: 'POST', body: { note } }),

  submitAvAddress: (personId: string, address: string, notes: string, caseRecordId?: string) =>
    request<{ taskId: string; notified: number }>(
      `/people/${encodeURIComponent(personId)}/address-submissions`,
      { method: 'POST', body: { address, notes, caseRecordId } }),

  reassignCase: (caseId: string, targetTeamId: string) =>
    request<{ ok?: true; error?: string; caseStatus?: string; routePlanId?: string | null }>(
      `/cases/${encodeURIComponent(caseId)}/reassign`,
      { method: 'POST', body: { targetTeamId } }),

  avTasks: () => request<{ items: Array<{
    id: string; status: string; caseNumber: string; submittedAt: string;
    submittedBy: string; proposedAddress: string; priorAddress: string | null;
    notes: string | null;
  }> }>('/av/tasks'),

  avNotificationCount: () => request<{ unread: number }>('/av/notifications/count'),

  startAvTask: (taskId: string) => request<{ ok?: true; error?: string }>(
    `/av/tasks/${encodeURIComponent(taskId)}/start`, { method: 'POST' }),

  decideAvTask: (taskId: string, body: {
    decision: string; notes: string; validatedAddress?: string;
    city?: string; state?: string; zip?: string; lat?: number; lng?: number;
  }) => request<{ ok?: true; error?: string; caseStatus?: string; teamId?: string }>(
    `/av/tasks/${encodeURIComponent(taskId)}/decision`, { method: 'POST', body }),

  teamRoute: (teamId: string, tripId?: string) =>
    request<import('../screens/TeamRoute.js').TeamRouteData>(
      `/routes/team?teamId=${encodeURIComponent(teamId)}` +
      (tripId ? `&tripId=${encodeURIComponent(tripId)}` : '')),

  reorderTeamRoute: (routePlanId: string, stopIds: string[]) =>
    request<import('../screens/TeamRoute.js').TeamRouteData>(
      `/routes/${encodeURIComponent(routePlanId)}/stops/order`,
      { method: 'PUT', body: { stopIds } },
    ),

  moveRouteStop: (routePlanId: string, stopId: string, targetRoutePlanId: string) =>
    request<{ ok?: true; error?: string }>(
      `/routes/${encodeURIComponent(routePlanId)}/stops/${encodeURIComponent(stopId)}/move`,
      { method: 'POST', body: { targetRoutePlanId } }),

  moveRouteStops: (routePlanId: string, stopIds: string[], targetRoutePlanId: string) =>
    request<{ ok?: true; error?: string }>(
      `/routes/${encodeURIComponent(routePlanId)}/stops/move-batch`,
      { method: 'POST', body: { stopIds, targetRoutePlanId } }),

  addTripToCalendar: (teamId: string) =>
    request<{ ok: true; ics: string }>('/calendar/trip',
      { method: 'POST', body: { teamId } }),

  map: () => request<import('../screens/Map.js').MapData>('/map'),

  // -------------------------------------------------------------------------
  // Import. §25 — parsing, validation and limits are all server-side.
  // -------------------------------------------------------------------------

  importFile: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/import/file', {
      method: 'POST',
      headers: csrfHeader(),
      credentials: 'same-origin',
      redirect: 'error',
      body: form,
    });
    if (!res.ok) throw new ApiError(res.status, (await res.json())?.reason);
    return (await res.json()) as import('../screens/Workspace.js').ImportPreview;
  },

  importPaste: (text: string) =>
    request<import('../screens/Workspace.js').ImportPreview>(
      '/import/paste', { method: 'POST', body: { text } }),

  importCommit: (jobId: string, mapping: unknown) =>
    request<{ ok: true; personsCreated: number; visitsCreated: number }>(
      '/import/commit', { method: 'POST', body: { jobId, mapping } }),

  unresolvedLocations: () =>
    request<{ items: import('../screens/Workspace.js').UnresolvedLocation[] }>(
      '/locations/unresolved'),

  updateLocation: (id: string, body: {
    address: string; city?: string; state?: string; zip?: string;
    lat?: number; lng?: number;
  }) => request<{ ok: true }>(`/locations/${encodeURIComponent(id)}`,
    { method: 'PATCH', body }),

  skipLocation: (id: string) =>
    request<{ ok: true }>(`/locations/${encodeURIComponent(id)}/skip`,
      { method: 'POST', body: {} }),

  calendar: (view: string, date: string) =>
    request<{
      periodLabel: string;
      events: import('../screens/Workspace.js').CalendarEventView[];
      connections: import('../screens/Workspace.js').CalendarConnectionView[];
      disclosureStatement: string;
      timeZone: string;
    }>(`/calendar?view=${encodeURIComponent(view)}&date=${encodeURIComponent(date)}`),

  profile: () => request<import('../screens/Workspace.js').ProfileData>('/me/profile'),

  help: () => request<{ topics: import('../screens/Workspace.js').HelpTopic[] }>('/help'),

  callCenter: () => isNativeMobile()
    ? nativeCallCenterSnapshot<import('../screens/CallCenter.js').CallCenterData>()
    : request<import('../screens/CallCenter.js').CallCenterData>('/call-center'),

  requestCommunicationAccess: () => request<{
    status: 'pending' | 'approved'; requestId?: string; veritasId: string;
  }>('/communications/access-request', { method: 'POST', body: {} }),

  networkPath: () => request<{
    provider: 'starlink' | 'other' | 'unknown';
    verifiedBy: 'starlink-geofeed' | null;
    checkedAt: string;
  }>('/call-center/network-path'),

  callCenterSession: () =>
    request<import('../hooks/useSipPhone.js').SipSessionConfiguration>(
      '/call-center/session', { method: 'POST', body: {} }),

  setCallCenterAgentStatus: (status: 'available' | 'break' | 'offline') =>
    request<{ ok: true }>('/call-center/agent-status', { method: 'POST', body: { status } }),

  recordCallCenterActivity: (kind:
    | 'operator_action' | 'call_control' | 'note_saved' | 'disposition_saved'
    | 'appointment_action' | 'state_resume' | 'keyboard_command') =>
    request<{
      ok: true; state: import('../screens/CallCenter.js').AgentStatus;
      reasonRequired: boolean; reasonDeadlineAt: string | null;
    }>('/call-center/activity', { method: 'POST', body: { kind } }),

  startCallCenterCall: (body: {
    target: string; direction: 'inbound' | 'outbound' | 'internal'; personId?: string;
  }) => request<{ id: string; sipTarget: string; providerToken: string }>(
    '/call-center/calls', { method: 'POST', body }),

  updateCallCenterCall: (id: string, body: {
    state?: 'ringing' | 'connected' | 'held' | 'wrap_up' | 'completed' | 'failed';
    disposition?: string; note?: string;
  }) => request<{ ok: true }>(`/call-center/calls/${encodeURIComponent(id)}`,
    { method: 'PATCH', body }),

  escalateCallCenterCall: (id: string, body: {
    reasonCode: string; reasonDetail?: string;
  }) => request<{
    ok: true; commandId: string;
    destination: { kind: 'queue'; name: string; extension: string };
  }>(`/call-center/calls/${encodeURIComponent(id)}/escalate`,
    { method: 'POST', body }),

  recordCallCenterDiagnostic: (body: import('../hooks/useSipPhone.js').PhoneDiagnostic) =>
    request<{ ok: true }>('/call-center/diagnostics', { method: 'POST', body }),

  reportMobileLocation: (body: {
    latitude: number; longitude: number; accuracy: number;
    altitude: number | null; heading: number | null; speed: number | null; reportedAt: string;
  }) => request<{ ok: true; reportedAt: string }>('/mobile/location', { method: 'POST', body }),

  wellnessNoteTemplates: () => request<import('../field-wellness/noteTemplates.js').NoteTemplateLibrary>(
    '/field-wellness/note-templates?classification=PROTECTED'),
  saveWellnessNoteTemplates: (body: { baseRevision: number; templates: import('../field-wellness/noteTemplates.js').NoteTemplate[] }) =>
    request<import('../field-wellness/noteTemplates.js').NoteTemplateLibrary>(
      '/admin/field-wellness/note-templates?classification=PROTECTED', { method: 'PUT', body }),
  fieldWellnessWorkspace: (q = '', view: 'cases' | 'drafts' = 'cases', offset = 0) => request<FieldWellnessWorkspace>(
    `/field-wellness/workspace?${new URLSearchParams({ q, view, offset: String(offset), classification: 'PROTECTED' })}`,
  ),

  startFieldWellnessDraft: (caseId: string) => request<FieldWellnessDraft>(
    '/field-wellness/drafts?classification=PROTECTED', { method: 'POST', body: { caseId } },
  ),

  fieldWellnessDraft: (draftId?: string) => request<FieldWellnessDraft>(
    '/field-wellness/draft?classification=PROTECTED' + (draftId ? `&draftId=${encodeURIComponent(draftId)}` : ''),
  ),

  saveFieldWellnessDraft: (body: {
    formVersion: string; answers: Record<string, string>; step: number;
    review: boolean; baseRevision: number; draftId?: string;
  }) => request<FieldWellnessDraft>(
    '/field-wellness/draft?classification=PROTECTED', { method: 'POST', body },
  ),

  submitFieldWellness: (body: {
    formVersion: string; answers: Record<string, string>; attested: true; draftId?: string; baseRevision?: number;
  }) => request<{
    id: string; reference: string;
    protectionProfile: 'aag_trident_fips203_204_205_v1';
  }>(
    '/field-wellness/submissions?classification=PROTECTED', { method: 'POST', body },
  ),

  createCallCenterTranscriptSegment: (callId: string, body: {
    speaker: 'agent' | 'caller';
    sequence: number;
    startedMs: number;
    endedMs: number;
    audioBase64: string;
  }) => request<{ segment: import('../screens/CallCenter.js').TranscriptSegment | null }>(
    `/call-center/calls/${encodeURIComponent(callId)}/transcript-segments`,
    { method: 'POST', body },
  ),

  callCenterTranscript: (callId: string) =>
    request<{ callId: string; segments: import('../screens/CallCenter.js').TranscriptSegment[] }>(
      `/call-center/calls/${encodeURIComponent(callId)}/transcript`,
    ),

  // -------------------------------------------------------------------------
  // Administration. Every one of these is permission-gated server-side. §35 §99
  // -------------------------------------------------------------------------

  fieldAdministrators: (query = '') =>
    request<import('../field-wellness/recipientTypes.js').FieldAdministratorView>(
      `/admin/field-administrators?classification=PROTECTED&q=${encodeURIComponent(query)}`,
    ),

  changeFieldAdministrator: (body: import('../field-wellness/recipientTypes.js').FieldAdministratorChange) =>
    request<{ ok: true }>('/admin/field-administrators?classification=PROTECTED', { method: 'POST', body }),

  admin: (tab: string) =>
    request<import('../screens/Admin.js').AdminData>(
      `/admin?tab=${encodeURIComponent(tab)}${['field-wellness', 'field-administrator', 'wellness-notes'].includes(tab) ? '&classification=PROTECTED' : ''}`),

  addFieldWellnessCaseOfficer: (body: { firstName: string; lastName: string }) =>
    request<{ officer: {
      id: string; label: string; maskedName: string; active: boolean;
    } }>('/admin/field-wellness/case-officers?classification=PROTECTED', {
      method: 'POST', body,
    }),

  selectFieldWellnessCaseOfficer: (officerId: string) =>
    request<{ ok: true }>('/admin/field-wellness/case-officer?classification=PROTECTED', {
      method: 'PUT', body: { officerId },
    }),

  createTeam: (name: string) =>
    request<{ team: { id: string; name: string } }>('/admin/teams', {
      method: 'POST', body: { name },
    }),

  managerGeography: () => request<{
    teams: Array<{ id: string; name: string }>;
    dimensions: Array<{ dimension: string; values: Array<{ value: string; count: number }> }>;
  }>('/admin/manager-geography'),

  stateTeams: () => request<{
    states: Array<{ state: string; people: number }>;
    teams: Array<{ id: string; name: string; capacity_minutes: number;
      base_label: string | null; base_lat: number | null; base_lng: number | null }>;
    assignments: Array<{ state: string; team_id: string }>;
  }>('/admin/state-teams'),

  setTeamBase: (teamId: string, label: string, lat: number, lng: number) =>
    request<{ ok: true; teamId: string; base: { label: string; lat: number; lng: number } }>(
      `/admin/teams/${encodeURIComponent(teamId)}/base`,
      { method: 'PUT', body: { label, lat, lng } }),

  setStateTeams: (state: string, teamIds: string[]) =>
    request<{ ok: true; state: string; teamIds: string[] }>(
      `/admin/state-teams/${encodeURIComponent(state)}`,
      { method: 'PUT', body: { teamIds } }),

  autoPlanState: (state: string, date: string,
    classification: 'LOGISTICS' | 'PROTECTED') =>
    request<{ error?: string; tripId?: string; stopCount?: number; teamCount?: number;
      estimated?: boolean }>('/trips/auto-plan',
      { method: 'POST', body: { state, date, classification } }),

  clusterPreview: (state: string, classification: 'LOGISTICS' | 'PROTECTED') =>
    request<{ error?: string; state: string; classification: string; algorithm: string;
      capacity: number; heldUnverifiedStops: number;
      teams: Array<{ id: string; name: string }>;
      clusters: Array<{ ordinal: number; fingerprint: string; stopCount: number;
        visitCount: number; places: string[] }> }>(
      `/trips/cluster-preview?state=${encodeURIComponent(state)}&classification=${classification}`),

  planReviewedCluster: (state: string, classification: 'LOGISTICS' | 'PROTECTED',
    date: string, fingerprint: string, teamIds: string[]) =>
    request<{ error?: string; message?: string; tripId?: string; stopCount?: number; teamCount?: number;
      estimated?: boolean }>('/trips/cluster-plan', { method: 'POST',
      body: { state, classification, date, fingerprint, teamIds } }),

  managerGeographyPreview: (dimension: string, value: string) => request<{
    dimension: string;
    value: string;
    fingerprint: string;
    people: number;
    routedStops: number;
    unresolvedStops: number;
    stops: Array<{
      stop_id: string; address: string; people: number; unresolved: boolean;
      current_teams: string[]; routed: boolean;
    }>;
  }>(`/admin/manager-geography/preview?dimension=${encodeURIComponent(dimension)}&value=${encodeURIComponent(value)}`),

  managerCreateTeam: (name: string) =>
    request<{ team: { id: string; name: string } }>('/admin/manager-teams', {
      method: 'POST', body: { name },
    }),

  managerAssignGeography: (body: { dimension: string; value: string;
    teamId: string | null; fingerprint: string }) =>
    request<{ ok: true; updated: number }>('/admin/manager-geography/assign', {
      method: 'POST', body,
    }),

  assignGeography: (body: { dimension: string; value: string; teamId: string | null }) =>
    request<{ ok: true; updated: number }>('/admin/geography/assign', {
      method: 'POST', body,
    }),

  connectStarlink: (body: { clientId: string; clientSecret: string }) =>
    request<{ ok: true }>('/admin/integrations/starlink', { method: 'POST', body }),

  disconnectStarlink: () =>
    request<{ ok: true }>('/admin/integrations/starlink', { method: 'DELETE' }),

  syncStarlink: () =>
    request<{ ok: true }>('/admin/integrations/starlink/sync', { method: 'POST', body: {} }),

  saveStarlinkAssignments: (
    assignments: Array<{ terminalId: string; teamId: string | null }>,
  ) => request<{ ok: true }>('/admin/integrations/starlink/assignments', {
    method: 'PUT', body: { assignments },
  }),

  runCallCenterDiagnostics: (target: import('../screens/Admin.js').DiagnosticTarget) =>
    request<import('../screens/Admin.js').DiagnosticRun>('/admin/call-center/diagnostics', {
      method: 'POST', body: { target },
    }),

  createUser: (body: { displayName: string; email: string; role: string }) =>
    request<{ id: string; invitation: 'sent' }>('/admin/users', {
      method: 'POST',
      body: { ...body, username: body.email },
    }),

  resendInvitation: (userId: string) =>
    request<{ ok: true; invitation: 'sent' }>(
      `/admin/users/${encodeURIComponent(userId)}/invitation`,
      { method: 'POST', body: {} },
    ),

  changeUserRole: (userId: string, role: string) =>
    request<{ ok: true }>('/admin/roles/permissions',
      { method: 'PUT', body: { targetUserId: userId, targetRoleName: role } }),

  setUserAvRole: (userId: string, enabled: boolean) =>
    request<{ ok: true; avRole: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/av-role`,
      { method: enabled ? 'POST' : 'DELETE', body: enabled ? {} : undefined }),

  changeUserScope: (userId: string, scope: string) =>
    request<{ ok: true }>('/admin/roles/permissions',
      { method: 'PUT', body: { targetUserId: userId, newScopeKind: scope } }),

  disableUser: (userId: string) =>
    request<{ ok: true }>(`/admin/users/${encodeURIComponent(userId)}/disable`,
      { method: 'POST' }),

  decideCommunicationAccess: (
    userId: string, decision: 'approve' | 'deny', reason?: string,
  ) => request<{ ok: true; status: 'approved' | 'denied' }>(
    `/admin/users/${encodeURIComponent(userId)}/communications-access`,
    { method: 'POST', body: { decision, reason } },
  ),

  unlockUser: (userId: string) =>
    request<{ ok: true; status: 'invited' | 'active'; loginLockoutCleared: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/unlock`,
      { method: 'POST' },
    ),

  revokeSession: (sessionId: string) =>
    request<{ ok: true }>(`/admin/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE' }),

  updateCallCenterRoster: (queueId: string, agentUserIds: string[]) =>
    request<{ ok: true }>('/admin/call-center/roster', {
      method: 'PUT', body: { queueId, agentUserIds },
    }),

  updateCallCenterPlaybook: (entries: import('../screens/CallCenter.js').PlaybookEntry[]) =>
    request<{ ok: true }>('/admin/call-center/playbook', {
      method: 'PUT', body: { entries },
    }),

  managerTransferCall: (id: string, body: {
    destinationKind: 'queue' | 'agent'; destinationId: string;
    reasonCode: string; reasonDetail?: string;
  }) => request<{
    ok: true; commandId: string;
    destination: { kind: 'queue' | 'agent'; name: string; extension: string };
  }>(`/admin/call-center/calls/${encodeURIComponent(id)}/transfer`, {
    method: 'POST', body,
  }),

  updateCallCenterEscalationTeam: (assignments: Array<{
    agentId: string; role: 'operator' | 'team_lead' | 'manager';
  }>) => request<{ ok: true }>('/admin/call-center/escalation-team', {
    method: 'PUT', body: { assignments },
  }),

  revokeAllSessions: () => request<{ revoked: number }>('/me/sessions', { method: 'DELETE' }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
};

/** Exposed for tests. Not used by application code. */
export function __setCsrfForTest(token: string | null) {
  csrfToken = token;
}
